import { Button, Tooltip } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { ChevronDown } from "lucide-react";
import { ICD10_QUICK_ACTIONS, type Icd10QuickAction } from "./quickActions";

interface Icd10QuickActionsProps {
  /** ICD-10 codes already on this episode of care. */
  appliedCodes: string[];
  onSelect: (action: Icd10QuickAction) => void;
  trigger?: React.ReactElement;
  triggerTooltip?: string;
}

/**
 * One-click adds for the administrative ICD-10 codes in `ICD10_QUICK_ACTIONS`.
 * A menu rather than inline buttons because the list is expected to grow —
 * adding a code should never require touching the layout.
 */
export const Icd10QuickActions = ({
  appliedCodes,
  onSelect,
  trigger,
  triggerTooltip,
}: Icd10QuickActionsProps) => {
  const menuTrigger = (
    <DropdownMenuTrigger asChild>
      {trigger ?? (
        <Button
          leftIcon={ChevronDown}
          variant="link"
          data-testid="icd10-quick-actions-trigger"
        >
          Quick Add
        </Button>
      )}
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {triggerTooltip ? (
        <Tooltip tooltipContentText={triggerTooltip}>{menuTrigger}</Tooltip>
      ) : (
        menuTrigger
      )}
      <DropdownMenuContent align="start" className="max-w-96 p-2 space-y-0.5">
        <DropdownMenuLabel className="text-tertiary">
          Administrative codes
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="-mx-2 my-1.5" />
        {ICD10_QUICK_ACTIONS.map((action) => {
          const alreadyAdded = appliedCodes.includes(action.code);
          return (
            <DropdownMenuItem
              key={action.code}
              disabled={alreadyAdded}
              className="gap-2"
              // Full ICD-10 wording is too long for the menu, but a clinician
              // picking between similar codes still needs to see it.
              title={`${action.code} - ${action.display}`}
              onSelect={() => onSelect(action)}
            >
              {action.label}
              <DropdownMenuShortcut>
                {alreadyAdded ? "Added" : action.code}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
